import { createStyles, makeStyles, Theme } from '@material-ui/core';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import MuiSyncIcon from '@material-ui/icons/Sync';
import React, { useState } from 'react';

import { BoardDetailsFragment } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexIconButtonHref } from '../../../shared/primitives/PlexIconButtonHref';
import { canDeleteBoard } from '../../permissions';
import { useDialogState } from '../useDialogState';
import { DeleteDialogContent } from './DeleteDialogContent';

type Props = {
  board: Omit<BoardDetailsFragment, 'position'>;
  onDelete?: (faqDestinationBoard: string, deleteAllFaqs: boolean) => void;
};

export function BoardActions({ board, onDelete }: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const classes = useStyles();

  const shouldDisableDelete = board.plex.defaultBoardId === board.id;
  const deleteIconTooltip = shouldDisableDelete ? "You can't delete a default board" : 'Delete';

  return (
    <div className={classes.root}>
      {board.source != null && (
        <PlexIconButtonHref
          className={classes.actionButton}
          MuiIcon={DescriptionIcon}
          href={board.source.url}
          color={ButtonColor.Purple}
          tooltip={'View Source - Google Document'}
        />
      )}
      {board.source != null && (
        <PlexIconButton
          className={classes.actionButton}
          MuiIcon={MuiSyncIcon}
          onClick={(): void => setDialogState({ type: 'sync_gdocs_board', boardId: board.id }, 'replace')}
          color={ButtonColor.Purple}
          tooltip={'Sync from Source - Google Document'}
        />
      )}
      {onDelete != null && canDeleteBoard(board.plex.currentUserRelation.state) && (
        <>
          <PlexIconButton
            className={classes.actionButton}
            disabled={shouldDisableDelete}
            onClick={(): void => setDeleteOpen(true)}
            MuiIcon={MuiDeleteIcon}
            color={ButtonColor.TransparentRed}
            tooltip={deleteIconTooltip}
          />
          <DeleteDialogContent
            currentBoardId={board.id}
            plexId={board.plexId}
            open={deleteOpen}
            onClose={(): void => setDeleteOpen(false)}
            onDelete={onDelete}
          />
        </>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      position: 'absolute',
      flexDirection: 'column',
      top: 0,
      right: 0,
    },
    actionButton: {
      marginTop: spacings.normal,
      marginRight: spacings.xlarge,
    },
  })
);
